/**
 * Motif partagé d'actions de ligne (listes)
 * - Boutons bordés au repos (fini le ghost transparent qui "ne fait pas bouton")
 * - Action principale = bouton coloré plein + libellé ; secondaires = icône bordée + tooltip
 * - Rangée nowrap (jamais de bouton orphelin qui wrappe seul)
 * Voir décisions figées dans btpal-tat-live (refonte actions de ligne).
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type RowActionTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'brand';

// Couleur du texte/icône par ton (le fond + la bordure en dérivent via currentColor).
// On évite les classes hex sur bg/border : le système d'override central d'index.css
// (`.dark .border-[#hex] { !important }`) écraserait tout `dark:` non-important.
const toneText: Record<RowActionTone, string> = {
    neutral: 'text-[#334155] dark:text-[#CBD5E1]',
    accent: 'text-[#1D4ED8] dark:text-[#93C5FD]',
    success: 'text-[#047857] dark:text-[#34D399]',
    warning: 'text-[#B45309] dark:text-[#FBBF24]',
    danger: 'text-[#DC2626] dark:text-[#F87171]',
    brand: 'text-[#0F2744] dark:text-[#CBD5E1]',
};

// Fond + bordure dérivés du texte via `.row-action-soft` (index.css, color-mix sur
// currentColor) : élévation constante quel que soit le fond de ligne (repos #1E293B →
// survol #334155 en sombre) → jamais d'inversion, et immune aux overrides hex ET à la
// règle universelle `* { border-color: var(--border) }`.
const softShared = 'row-action-soft focus-visible:ring-current';

const toneSolid: Record<RowActionTone, string> = {
    neutral: 'border-[#334155] bg-[#334155] text-white hover:bg-[#1E293B] focus-visible:ring-[#334155] dark:border-[#475569] dark:bg-[#475569] dark:hover:bg-[#334155]',
    accent: 'border-[#1D4ED8] bg-[#1D4ED8] text-white hover:bg-[#1E40AF] focus-visible:ring-[#1D4ED8] dark:border-[#2563EB] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]',
    success: 'border-[#047857] bg-[#047857] text-white hover:bg-[#065F46] focus-visible:ring-[#047857] dark:border-[#059669] dark:bg-[#059669] dark:hover:bg-[#047857]',
    warning: 'border-[#B45309] bg-[#B45309] text-white hover:bg-[#92400E] focus-visible:ring-[#B45309] dark:border-[#D97706] dark:bg-[#D97706] dark:hover:bg-[#B45309]',
    danger: 'border-[#DC2626] bg-[#DC2626] text-white hover:bg-[#B91C1C] focus-visible:ring-[#DC2626] dark:border-[#EF4444] dark:bg-[#EF4444] dark:hover:bg-[#DC2626]',
    brand: 'border-[#0F2744] bg-[#0F2744] text-white hover:bg-[#091A2E] focus-visible:ring-[#0F2744] dark:border-[#2563EB] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]',
};

const rowActionBase =
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border text-xs font-medium leading-none cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 dark:focus-visible:ring-offset-[#0F172A]';

// Tailles : `default` = tables desktop (h-8). `touch` = cartes/grilles où la cible
// doit faire 44px sur mobile (accessibilité tap-target) et se resserrer sur desktop.
export type RowActionSize = 'default' | 'touch';

const sizeIcon: Record<RowActionSize, string> = {
    default: 'h-8 w-8 p-0',
    touch: 'h-11 w-11 p-0 sm:h-9 sm:w-9',
};
const sizeLabeled: Record<RowActionSize, string> = {
    default: 'h-8 px-2.5',
    touch: 'h-11 px-3 sm:h-9 sm:px-2.5',
};

interface RowActionShape {
    tone?: RowActionTone;
    solid?: boolean;
    iconOnly?: boolean;
    size?: RowActionSize;
}

/** Classes du motif, exposées pour styler un élément non-bouton (ex: <label> d'upload). */
export function rowActionClasses({ tone = 'neutral', solid = false, iconOnly = false, size = 'default' }: RowActionShape): string {
    return cn(
        rowActionBase,
        iconOnly ? sizeIcon[size] : sizeLabeled[size],
        solid ? toneSolid[tone] : cn(softShared, toneText[tone]),
    );
}

export interface RowActionButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    icon: LucideIcon;
    label: string;
    tone?: RowActionTone;
    solid?: boolean;
    /** N'affiche que l'icône, expose le libellé en tooltip + aria-label. */
    iconOnly?: boolean;
    /** `touch` = cible 44px mobile (cartes/grilles), resserrée sur desktop. */
    size?: RowActionSize;
    isLoading?: boolean;
}

export const RowActionButton = forwardRef<HTMLButtonElement, RowActionButtonProps>(
    ({ icon: Icon, label, tone = 'neutral', solid = false, iconOnly = false, size = 'default', isLoading = false, className, disabled, ...props }, ref) => {
        return (
            <button
                ref={ref}
                type="button"
                className={cn(rowActionClasses({ tone, solid, iconOnly, size }), className)}
                disabled={disabled || isLoading}
                aria-label={iconOnly ? label : undefined}
                title={iconOnly ? label : undefined}
                {...props}
            >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4 shrink-0" />}
                {!iconOnly && <span>{label}</span>}
            </button>
        );
    },
);
RowActionButton.displayName = 'RowActionButton';

export function RowActions({ children, className }: { children: ReactNode; className?: string }): ReactNode {
    return (
        <div className={cn('flex items-center justify-end gap-1.5 flex-nowrap', className)}>
            {children}
        </div>
    );
}
